import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { emailSequencesAPI } from '@/lib/api';
import EmailBodyEditor from '@/components/settings/EmailBodyEditor';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Loader2, Mail, Eye, Save, RotateCcw, Send, Code2 } from 'lucide-react';

export default function EmailSequencesTab() {
  const { isAdmin, user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [isCustom, setIsCustom] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [firstName, setFirstName] = useState('Haroon');
  const [planName, setPlanName] = useState('POA');
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (user?.email) setTestEmail(user.email);
  }, [user?.email]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailSequencesAPI.list();
      const items = res.data?.templates || [];
      setList(items);
      setSelectedKey((prev) => prev || items[0]?.key || null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    loadList();
    return undefined;
  }, [isAdmin, loadList]);

  const loadDetail = useCallback(async (key) => {
    if (!key) return;
    setLoadingDetail(true);
    setPreviewHtml('');
    try {
      const res = await emailSequencesAPI.get(key);
      const t = res.data?.template;
      setSubject(t?.subject || '');
      setBodyHtml(t?.body_html || '');
      setPlaceholders(t?.placeholders || []);
      setIsCustom(!!t?.is_custom);
      setEditorKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load template');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (selectedKey) loadDetail(selectedKey);
  }, [selectedKey, loadDetail]);

  const selectedMeta = useMemo(
    () => list.find((t) => t.key === selectedKey),
    [list, selectedKey]
  );

  if (!isAdmin) return null;

  const handlePreview = async () => {
    if (!selectedKey) return;
    setPreviewing(true);
    try {
      const res = await emailSequencesAPI.preview(selectedKey, {
        subject,
        body_html: bodyHtml,
        first_name: firstName,
        plan_name: planName,
      });
      setPreviewHtml(res.data?.html || '');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (!selectedKey) return;
    setSaving(true);
    try {
      await emailSequencesAPI.update(selectedKey, { subject, body_html: bodyHtml });
      toast.success('Template saved');
      setIsCustom(true);
      await loadList();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedKey) return;
    if (!window.confirm(`Reset ${selectedKey.toUpperCase()} to the default Blade template?`)) return;
    setSaving(true);
    try {
      const res = await emailSequencesAPI.reset(selectedKey);
      const t = res.data?.template;
      setSubject(t?.subject || '');
      setBodyHtml(t?.body_html || '');
      setIsCustom(false);
      setEditorKey((k) => k + 1);
      toast.success('Reset to default');
      await loadList();
      setPreviewHtml('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!selectedKey || !testEmail) return;
    setSending(true);
    try {
      await emailSequencesAPI.sendTest(selectedKey, {
        email: testEmail,
        subject,
        body_html: bodyHtml,
        first_name: firstName,
        plan_name: planName,
      });
      toast.success(`Test sent to ${testEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border/80">
        <CardContent className="p-6 space-y-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <Card className="bg-card border-border/80 lg:col-span-3">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            Sequences
          </CardTitle>
          <CardDescription className="text-xs">Transactional emails (A1, A3, B6, B7…)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 p-3 pt-0">
          {list.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setSelectedKey(t.key)}
              className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors border ${
                selectedKey === t.key
                  ? 'bg-muted/60 border-border'
                  : 'border-transparent hover:bg-muted/30'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold uppercase tracking-wide">{t.key}</span>
                {t.is_custom && (
                  <Badge variant="secondary" className="text-[10px]">Custom</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.subject}</p>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-card border-border/80 lg:col-span-5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">
            {selectedKey ? selectedKey.toUpperCase() : 'Select a template'}
            {isCustom && (
              <Badge className="ml-2 grad-accent text-white border-0 text-[10px]">Customized</Badge>
            )}
          </CardTitle>
          <CardDescription className="text-xs">
            {selectedMeta?.category || '—'} · Edit text visually, insert placeholders from the chips
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingDetail ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <>
              <div className="space-y-2">
                <Label className="text-sm">Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="bg-background/40 border-border h-10"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-sm">Body</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-muted-foreground"
                    onClick={() => setShowHtmlSource((v) => !v)}
                  >
                    <Code2 className="h-3.5 w-3.5 mr-1" />
                    {showHtmlSource ? 'Visual editor' : 'HTML source'}
                  </Button>
                </div>
                {showHtmlSource ? (
                  <Textarea
                    value={bodyHtml}
                    onChange={(e) => setBodyHtml(e.target.value)}
                    className="min-h-[280px] font-mono text-xs bg-background/40 border-border"
                  />
                ) : (
                  <EmailBodyEditor
                    key={`${selectedKey}-${editorKey}`}
                    value={bodyHtml}
                    onChange={setBodyHtml}
                    placeholders={placeholders}
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Preview first_name</Label>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="h-9 bg-background/40" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Preview plan_name</Label>
                  <Input value={planName} onChange={(e) => setPlanName(e.target.value)} className="h-9 bg-background/40" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" className="grad-accent text-white border-0" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span className="ml-1.5">Save</span>
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={handlePreview} disabled={previewing}>
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                  <span className="ml-1.5">Preview</span>
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={handleReset} disabled={saving || !isCustom}>
                  <RotateCcw className="h-4 w-4" />
                  <span className="ml-1.5">Reset</span>
                </Button>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-border/60">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="h-9 bg-background/40"
                />
                <Button type="button" size="sm" variant="secondary" onClick={handleSendTest} disabled={sending || !testEmail}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  <span className="ml-1.5">Send test</span>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border/80 lg:col-span-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Live preview</CardTitle>
          <CardDescription className="text-xs">Rendered with sample placeholders</CardDescription>
        </CardHeader>
        <CardContent>
          {previewHtml ? (
            <iframe
              title="Email preview"
              srcDoc={previewHtml}
              className="w-full h-[560px] rounded-lg border border-border/60 bg-[#080b11]"
              sandbox="allow-same-origin allow-popups"
            />
          ) : (
            <div className="h-[560px] rounded-lg border border-dashed border-border/60 flex items-center justify-center text-sm text-muted-foreground px-4 text-center">
              Click Preview to render this template
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
